import React, { createContext, type ReactNode, useEffect } from 'react';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';

export const EmbedModeContext: React.Context<boolean> = createContext<boolean>(false);
const { Provider } = EmbedModeContext;

type Props = {
	children: ReactNode;
};

const handleEmbedLinkClick = (e: Event) => {
	if (fg('jsc_nin_smart_link_fast_follows')) {
		if (e.target instanceof Element) {
			const anchor = e.target.closest('a'); // Find closest parent anchor (including the target itself)
			if (anchor && anchor.target !== '_blank') {
				// link
				anchor.target = '_blank';
			}
		}
	} else {
		const eventTarget: EventTarget | null = e.target;

		if (
			eventTarget &&
			eventTarget instanceof HTMLAnchorElement &&
			eventTarget.target !== '_blank'
		) {
			// link
			eventTarget.target = '_blank';
		}
	}
};

// When issue view is loaded within an iframe, it needs to follow the proper
// element elevation principles, found here: https://hello.atlassian.net/wiki/spaces/~460573267/pages/2355983526/Jira+Dark+Mode+-+UI+Guidelines#Element-elevation-basics
// Unfortunately the current elevation is set in the global-container, which is loaded before this Provider so
// we have to override the overrider to ensure that the proper elevation is set
const DarkModeMonolithStyleOverride = () => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
	<style
		// eslint-disable-next-line react/no-danger
		dangerouslySetInnerHTML={{
			__html: `
                    #jira-frontend {
                        background: ${token('elevation.surface.overlay')};
                    }
                `,
		}}
	/>
);

export const EmbedModeProvider = ({ children }: Props): React.JSX.Element => {
	useEffect(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('click', handleEmbedLinkClick, true);
		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('click', handleEmbedLinkClick);
		};
	}, []);

	return (
		<>
			<DarkModeMonolithStyleOverride />
			<Provider value>{children}</Provider>
		</>
	);
};
